import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, OnDestroy, inject, input } from '@angular/core';
import { getUVEState } from '@dotcms/uve';
import { Observable, ReplaySubject, Subject, combineLatest, map, mergeMap } from 'rxjs';

import { AppFacade } from 'ish-core/facades/app.facade';
import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { whenTruthy } from 'ish-core/utils/operators';

import { DotCmsAnimationComponent } from '../../animations/animation/dot-cms-animation.component';
import { getFieldAnimation } from '../../animations/dot-cms-aniamtion.helper';
import { DotCmsUveFacade } from '../../facades/dot-cms-uve.facade';
import { Banner } from '../../models/dot-cms-uve-contentlet.model';
import { DotCmsCodecEncodePipe } from '../../pipes/dot-cms-codec-encode.pipe';

import { DotCmsUveBannerLinkComponent } from './dot-cms-uve-banner-link/dot-cms-uve-banner-link.component';

@Component({
  selector: 'custom-dot-cms-uve-banner',
  templateUrl: './dot-cms-uve-banner.component.html',
  styleUrls: ['./dot-cms-uve-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, AsyncPipe, DotCmsCodecEncodePipe, DotCmsUveBannerLinkComponent, DotCmsAnimationComponent],
})
export class DotCmsUveBannerComponent implements OnChanges, OnDestroy {
  imageUrl = new ReplaySubject<string>(1);
  getFieldAnimation = getFieldAnimation;
  readonly contentlet = input<Banner>(undefined);
  deviceType$: Observable<DeviceType>;
  isInSideEditor: boolean;

  private destroy$ = new Subject<void>();
  triggerCount = 1;
  private dotCmsUveFacade = inject(DotCmsUveFacade);
  private appFacade = inject(AppFacade);
  private gtmFacade = inject(GoogleTagManagerFacade);
  private elementRef = inject(ElementRef);

  ngOnChanges() {
    this.deviceType$ = this.appFacade.deviceType$;
    this.gtmFacade.setupIntersectionObserverForGTMEvent(
      this.elementRef.nativeElement,
      [this.contentlet()],
      this.triggerCount,
      'view_promotion'
    );
    this.isInSideEditor = !!getUVEState();
  }

  /* layout styling methods starts here*/
  getColumnCss(layout: string): string {
    switch (layout) {
      case '1': {
        return 'col-lg-9 text-center';
      }
      case '2': {
        return 'col-lg-7 text-center';
      }
      case '3': {
        return 'text-center';
      }
      default: {
        return 'col-lg-7 text-center';
      }
    }
  }
  getRowCss(layout: string): string {
    switch (layout) {
      case '1': {
        return 'row justify-content-center';
      }
      case '2': {
        return 'row justify-content-start';
      }
      case '3': {
        return 'banner-three-wrapper';
      }
      default: {
        return 'row justify-content-end';
      }
    }
  }
  getBannerCss(layout: string): string {
    switch (layout) {
      case '1': {
        return 'caption-banner';
      }
      case '2': {
        return 'caption-banner dot-cms-banner';
      }
      case '3': {
        return 'caption-banner';
      }
      default: {
        return 'caption-banner dot-cms-banner-four';
      }
    }
  }
  /* layout styling methods ends here*/

  getImageSource(): Observable<string> {
    return this.getBannerImageURI();
  }
  bannerOverlay(contentlet: Banner): string {
    const bannerContent = contentlet?.buttonText || contentlet?.link || contentlet?.title || contentlet?.caption;
    if (bannerContent) {
      return 'banner-overlay';
    }
    return;
  }
  bannerWidth(contentlet: Banner): string {
    if (contentlet?.maxWidth?.length > 0) {
      return 'banner-width';
    }
    return;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  captionBackgroundHide(contentlet: Banner, layout: string): string {
    const content = contentlet?.buttonText || contentlet?.link || contentlet?.title || contentlet?.caption;

    if (!content && (layout === '2' || layout === '4')) {
      return 'caption-background-hide';
    }

    return null;
  }
  getBannerImageURI(): Observable<string> {
    return this.appFacade.deviceType$.pipe(
      mergeMap(deviceType => {
        const imageKey = this.generateImageKeyForBannerUsingDeviceType(deviceType);
        return this.generateImageURIFromImageKey(imageKey);
      })
    );
  }
  generateImageKeyForBannerUsingDeviceType(deviceType: DeviceType) {
    const imageSize =
      deviceType === 'desktop'
        ? 'lg'
        : deviceType === 'tablet'
        ? //@ts-ignore
          this.isString(this.contentlet().imagemd)
          ? 'md'
          : 'lg'
        : deviceType === 'mobile'
        ? //@ts-ignore
          this.isString(this.contentlet().imagesm)
          ? 'sm'
          : //@ts-ignore
          this.isString(this.contentlet().imagemd)
          ? 'md'
          : 'lg'
        : '';
    return `image${imageSize}`;
  }

  private isString(object: {}) {
    return typeof object === 'string';
  }
  private generateImageURIFromImageKey(imagekey: string): Observable<string> {
    return combineLatest([this.dotCmsUveFacade.getDotCmsHost$.pipe(whenTruthy())]).pipe(
      whenTruthy(),
      map(([host]) => {
        const content = this.contentlet() as unknown as Record<string, any>;
        const imageObj = content ? content[imagekey] : undefined;
        const idPath: string = imageObj && typeof imageObj.idPath === 'string' ? imageObj.idPath : '';

        // If idPath already contains the expected dA path structure, use original
        if (idPath.startsWith('/dA/')) {
          if (this.isInSideEditor) {
            return `${host}${idPath}?publish=${this.contentlet()?.publish}`;
          }
          return `${host}${idPath}`;
        }

        // Otherwise, process idPath to build proper URL
        const pathParts = idPath.split('/');

        // Defensive: ensure the uuid part exists at expected index
        if (!content?.creationDate) {
          const relativePath = pathParts.slice(7).join('/');

          return `${host}/dA/${this.contentlet().inode}/${relativePath}`;
        }

        // fallback to original if conditions don't match
        return `${host}${idPath}`;
      })
    );
  }
}
